import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { BookOpen, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface EvidenceViewerProps {
  evidenceText: string;
  /** Section or page reference in the original contract */
  contractRef: string | null;
  /** KB source document name (compliance findings only) */
  kbSource?: string | null;
  /** Article reference within the KB source, e.g. "Art. 28(3)" */
  kbArticle?: string | null;
  /** GROUNDED = model produced with supporting evidence; MANUAL_REVIEW = low-confidence */
  groundingStatus: 'GROUNDED' | 'MANUAL_REVIEW';
  className?: string;
}

/**
 * Displays a contract text excerpt with optional KB source reference and
 * grounding status badge. Used inline within FindingCard.
 */
export function EvidenceViewer({
  evidenceText,
  contractRef,
  kbSource,
  kbArticle,
  groundingStatus,
  className,
}: EvidenceViewerProps) {
  const isGrounded = groundingStatus === 'GROUNDED';
  const hasExcerpt = Boolean(evidenceText && evidenceText.trim().length > 0);

  return (
    <Card className={cn('border-l-4', isGrounded ? 'border-l-green-500' : 'border-l-amber-500', className)}>
      <CardContent className="pt-4 pb-3 space-y-3">

        {/* Contract text excerpt — only render when present; an empty quote
            block with just &ldquo;&rdquo; reads as a UI bug to reviewers. */}
        {hasExcerpt ? (
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
              <FileText className="h-3 w-3" aria-hidden="true" />
              Contract excerpt
              {contractRef && (
                <span className="normal-case ml-1">— {contractRef}</span>
              )}
            </p>
            <blockquote className="text-sm italic text-foreground bg-muted/50 rounded px-3 py-2 border border-muted">
              &ldquo;{evidenceText}&rdquo;
            </blockquote>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground italic">
            No contract excerpt available — this finding was raised by the pipeline itself (e.g. agent failure or rule violation without a contract quote).
          </p>
        )}

        {/* KB source (compliance findings only) */}
        {kbSource && (
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1 flex items-center gap-1">
              <BookOpen className="h-3 w-3" aria-hidden="true" />
              Legal reference
            </p>
            <p className="text-sm text-foreground">
              <span className="font-medium">{kbSource}</span>
              {kbArticle && <span className="text-muted-foreground"> — {kbArticle}</span>}
            </p>
          </div>
        )}

        {/* Grounding badge */}
        <div className="flex items-center gap-1.5">
          {isGrounded ? (
            <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400" aria-hidden="true" />
          ) : (
            <AlertCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" aria-hidden="true" />
          )}
          <Badge
            variant="outline"
            className={cn(
              'text-xs',
              isGrounded
                ? 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-400'
                : 'border-amber-300 text-amber-700 dark:border-amber-700 dark:text-amber-400',
            )}
          >
            {isGrounded ? 'GROUNDED' : 'MANUAL REVIEW'}
          </Badge>
        </div>

      </CardContent>
    </Card>
  );
}
